
import _ from 'lodash';

// DOC: reference https://www.learnjsonschema.com/2020-12/

const createBaseValidation = (propertyName, property, json) => {
  const validation = {
    validation: {}
  };

  // required
  if (_.isArray(json.required) && json.required.includes(propertyName)) {
    validation.required = true;
  }

  // minLength and maxLength
  if (_.isNumber(property.minLength)) {
    validation.validation.minLength = property.minLength;
  }
  if (_.isNumber(property.maxLength)) {
    validation.validation.maxLength = property.maxLength;
  }

  // maxItems and minItems
  if (_.isNumber(property.maxItems)) {
    validation.validation.minLength = property.maxItems;
  }
  if (_.isNumber(property.minItems)) {
    validation.validation.maxLength = property.minItems;
  }

  // minimum and maximum
  if (_.isNumber(property.minimum)) {
    validation.validation.min = property.minimum;
  }
  if (_.isNumber(property.maximum)) {
    validation.validation.max = property.maximum;
  }

  // regex
  if (_.isString(property.pattern)) {
    validation.validation.pattern = property.pattern;
  }

  return validation;
};


/**
 * createBaseField
 * Create base properties for a field
 * @param {*} property
 * @returns
 */
const createBaseField = (propertyName, property, json) => {

  const field = {};

  if (property.title) {
    field.label = property.title;
  } else {
    field.label = propertyName;
  }
  if (property.description) {
    field.hint = property.description;
  }
  if (property.readOnly) {
    field.readOnly = true;
  }

  return field;
};


/**
 * constOverrides
 * Get overrides for "const"
 * @param {*} property
 * @returns
 */
const constOverrides = property => {
  let constOverrides = {};
  if (!property.cost) {
    constOverrides.readOnly = true;
  }
  return constOverrides;
};


/**
 * enumOverrides
 * Get overrides for enum
 * @param {*} property
 * @returns
 */
const enumOverrides = (property, { json, errors, warnings }) => {
  let enumOverrides = {};
  if (_.isArray(property.enum) && !_.isEmpty(property.enum)) {
    enumOverrides = {
      component: 'select',
      options: property.enum
        .map(opt => {
          if (_.isString(opt) || _.isNumber(opt)) {
            return { label: opt, value: opt };
          }
          warnings.push(`Only string and number values for "enum" key (${JSON.stringify(opt)})`);
          return null;
        })
        .filter(Boolean)
    }
  }
  return enumOverrides;
};


const StringTransformer = (propertyName, propertyRaw, { json, errors, warnings }) => {
  // expand refs
  const property = resolveReferences(propertyRaw, json);
  // check errors and warnings
  checkErrorsAndWarnings(property, errors, warnings, propertyName);

  return {
    component: 'input-text',
    name: propertyName,
    ...createBaseField(propertyName, property, json),
    ...createBaseValidation(propertyName, property, json),
    ...enumOverrides(property, { json, errors, warnings }),
    ...constOverrides(property, { json, errors, warnings })
  };
};


const IntegerTransformer = (propertyName, propertyRaw, { json, errors, warnings }) => {
  // expand refs
  const property = resolveReferences(propertyRaw, json);
  // check errors and warnings
  checkErrorsAndWarnings(property, errors, warnings, propertyName);

  const integerProps = {};
  // minimum and maximum
  if (_.isNumber(property.minimum)) {
    integerProps.min = property.minimum;
  }
  if (_.isNumber(property.maximum)) {
    integerProps.max = property.maximum;
  }


  return {
    component: 'input-number',
    name: propertyName,
    ...createBaseField(propertyName, property, json),
    ...createBaseValidation(propertyName, property, json),
    ...integerProps,
    ...enumOverrides(property, { json, errors, warnings }),
    ...constOverrides(property, { json, errors, warnings })
  };
};


const ObjectTransformer = (propertyName, propertyRaw, { json, errors, warnings }) => {
  // expand refs
  const property = resolveReferences(propertyRaw, json);
  // check errors and warnings
  checkErrorsAndWarnings(property, errors, warnings, propertyName);

  return {
    component: 'object',
    name: propertyName,
    ...createBaseField(propertyName, property, json),
    ...createBaseValidation(propertyName, property, json),
    fields: transformProperties(property.properties, { json, errors, warnings }),
    collapsible: false,
    open: true
  };
};


const BooleanTransformer = (propertyName, propertyRaw, { json, errors, warnings }) => {
  // expand refs
  const property = resolveReferences(propertyRaw, json);
  // check errors and warnings
  checkErrorsAndWarnings(property, errors, warnings, propertyName);

  return {
    component: 'checkbox',
    name: propertyName,
    ...createBaseField(propertyName, property, json),
    ...constOverrides(property)
  };
};


const resolveReferences = (obj, json) => {
  if (obj['$ref']) {
    // split the path and remove initial #
    const splittedPath = obj['$ref'].split('/').filter(s => s !== '#');

    // if path empty return unchanged object
    if (splittedPath.length !== 0) {
      // resolve the referenced object
      const referenceObj = splittedPath.reduce(
        (acc, key) => {
          return acc ? acc[key] : null;
        },
        json
      );

      // omit the $ref and glue the found object
      return {
        ..._.omit(obj, '$ref'),
        ...referenceObj
      };
    }
  };

  return obj;
};


const SUPPORTED_ARRAY_VALUES = ['string', 'boolean', 'integer', 'number']

/**
 * ArrayTransformer
 * Transformer for array
 * @param {*} propertyName
 * @param {*} propertyRaw
 * @param {*} param2
 * @returns
 */
const ArrayTransformer = (propertyName, propertyRaw, { json, errors, warnings }) => {
  // expand refs
  const property = resolveReferences(propertyRaw, json);
  // check errors and warnings
  checkErrorsAndWarnings(property, errors, warnings, propertyName);

  // not supporting prefixItems
  if (_.isArray(property.prefixItems) && !_.isEmpty(property.prefixItems)) {
    errors.push('Array only support elements of the same type, generally objects, "prefixItems" is not supported');
    return null;
  }

  const items = resolveReferences(property.items, json);

  if (!items.type) {
    errors.push(`Missing items.type in array "${propertyName}"`);
    return null;
  }

  const arrayProps = {};
  if (items.type === 'object') {
    arrayProps.arrayType = 'arrayOfObject';
    arrayProps.fields = transformProperties(items.properties, { json, errors, warnings });

  } else if (SUPPORTED_ARRAY_VALUES.includes(items.type)) {
    arrayProps.arrayType = 'arrayOfValues';
    arrayProps.fields = transformProperties(
      {
        [`${propertyName}_array_field`]: items
      },
      { json, errors, warnings }
    );
    arrayProps.fields[0].label = undefined;
  } else {
    errors.push(`Unsupported type (${items.type}) inside array`);
    return null;
  }

  // arrayOfValues
  return {
    component: 'array',
    name: propertyName,
    ...createBaseField(propertyName, property, json),
    ...createBaseValidation(propertyName, property, json),
    ...arrayProps
  };
};

// list of transformers
const JSONSchemaTransformers = {
  'string': StringTransformer,
  'integer': IntegerTransformer,
  'number': IntegerTransformer,
  'object': ObjectTransformer,
  'boolean': BooleanTransformer,
  'array': ArrayTransformer
};

/**
 * transformProperties
 * Transform object of properties into an array of fields
 * @param {*} properties
 * @param {*} param1
 * @returns
 */
const transformProperties = (properties, { json, errors, warnings }) => {
  return Object.keys(properties)
    .map(propertyName => {
      const property = properties[propertyName];

      let field;
      if (property.type === undefined) {
        field = JSONSchemaTransformers['string'](propertyName, property, { json, errors, warnings });
      } else if (JSONSchemaTransformers[property.type]) {
        field = JSONSchemaTransformers[property.type](propertyName, property, { json, errors, warnings });
      } else {
        errors.push(`Unknows type "${property.type}"`);
      }

      return field;
    })
    .filter(Boolean);
};


const UNSUPPORTED_KEYWORDS_WARNINGS = ['if', 'then', 'else', 'not', 'allOf', 'oneOf', 'anyOf'];

/**
 * checkErrorsAndWarnings
 * Check for erorrs and warnings inside an object, neglect nesting
 * @param {*} json
 * @param {*} errors
 * @param {*} warnings
 * @param {*} path
 */
const checkErrorsAndWarnings = (json, errors, warnings, path) => {
  // check for unsupported keywords
  UNSUPPORTED_KEYWORDS_WARNINGS.forEach(keyword => {
    if (json[keyword]) {
      warnings.push(`Unsupported keyword "${keyword}" in JSON schema "${path}"`);
    }
  });
};



const JSONSchemaToLetsForm = (json, framework) => {
  const errors = [];
  const warnings = [];
  let form;

  // check for root errors
  checkErrorsAndWarnings(json, errors, warnings, 'root');

  if (json.type !== 'object') {
    errors.push(`LetsForm supports only "object" root type`);
  } else {
    form = {
      '$schema': `https://cdn.jsdelivr.net/npm/lets-form/schemas/${framework}/form.json`,
      version: 2,
      layout: 'vertical',
      fields: transformProperties(json.properties, { json, errors, warnings })
    };
  }

  return {
    form,
    errors,
    warnings
  };
};

export { JSONSchemaToLetsForm };
